import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
} from "react";
import type { ComponentName, ComponentState } from "app/componentOptions";
import { atom, useAtomValue, useStore, type PrimitiveAtom } from "jotai";
import type { ComponentStackEntry } from "app/uiState/mobile/componentStackAtom";
import {
  createComponentController,
  type ComponentController,
} from "lib/util/ComponentController";

type CurrentRoutedComponentContextValue = {
  componentState: ComponentState<ComponentName>;
  setComponentState: Dispatch<SetStateAction<ComponentState<ComponentName>>>;
  closeImmediately: () => void;
};

export const CurrentRoutedComponentContext =
  createContext<CurrentRoutedComponentContextValue | null>(null);

export type RoutedComponentStackEntry = {
  stackEntry: ComponentStackEntry;
  componentState: ComponentState<ComponentName>;
  setComponentState: Dispatch<SetStateAction<ComponentState<ComponentName>>>;
  controller: ComponentController;
  closeImmediately: () => void;
};

type RoutedComponentStackContextValue = {
  entries: RoutedComponentStackEntry[];
  setRouteComponent: (componentState: ComponentState<ComponentName>) => void;
  clearRouteComponents: () => void;
};

const RoutedComponentStackContext =
  createContext<RoutedComponentStackContextValue | null>(null);

const emptyComponentAtom = atom<ComponentState<ComponentName> | null>(null);

export function RoutedComponentStackProvider({ children }: PropsWithChildren) {
  const store = useStore();
  const [stackEntry, setStackEntry] = useState<{
    componentAtom: PrimitiveAtom<ComponentState<ComponentName>>;
    controller: ComponentController;
  } | null>(null);
  const componentState = useAtomValue(
    stackEntry?.componentAtom ?? emptyComponentAtom,
  );

  const updateComponentState = useCallback<
    Dispatch<SetStateAction<ComponentState<ComponentName>>>
  >(
    (nextState) => {
      if (stackEntry) store.set(stackEntry.componentAtom, nextState);
    },
    [stackEntry, store],
  );

  const clearRouteComponents = useCallback(() => setStackEntry(null), []);
  const setRouteComponent = useCallback(
    (nextState: ComponentState<ComponentName>) => {
      if (
        stackEntry &&
        store.get(stackEntry.componentAtom).componentId ===
          nextState.componentId
      ) {
        store.set(stackEntry.componentAtom, nextState);
        return;
      }
      setStackEntry({
        componentAtom: atom(nextState),
        controller: createComponentController(nextState.componentId),
      });
    },
    [stackEntry, store],
  );

  const entries = useMemo<RoutedComponentStackEntry[]>(() => {
    if (!componentState || !stackEntry) return [];
    return [
      {
        stackEntry,
        componentState,
        setComponentState: updateComponentState,
        controller: stackEntry.controller,
        closeImmediately: clearRouteComponents,
      },
    ];
  }, [componentState, stackEntry, updateComponentState, clearRouteComponents]);

  const value = useMemo<RoutedComponentStackContextValue>(
    () => ({ entries, setRouteComponent, clearRouteComponents }),
    [entries, setRouteComponent, clearRouteComponents],
  );

  return createElement(
    RoutedComponentStackContext.Provider,
    { value },
    children,
  );
}

export function useRoutedComponentStack() {
  const context = useContext(RoutedComponentStackContext);

  if (!context) {
    throw new Error("RoutedComponentStackContext not found");
  }

  return context;
}

export function useOptionalRoutedComponentStack() {
  return useContext(RoutedComponentStackContext);
}
